import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { API_URL, StatusBadge } from './AdminShared';
import {
  Users, Home, CreditCard, Shield, FileCheck, CheckCircle, XCircle,
  Clock, AlertTriangle, Eye, Lock, Trash2, Gift, Share2
} from 'lucide-react';

export const InvitationMessages = ({ siteUrl, freeTrial }) => {
  const [copiedType, setCopiedType] = useState(null);
  const messages = {
    seller: `List your property on Shopperden — with 3D virtual tours, AI valuation, and verified buyer matching.\n\nStart here: ${siteUrl}/register?role=seller\n\nIt's${freeTrial ? ' free to start' : ' simple to get started'}.`,
    buyer: `Looking for a property? Shopperden lets you explore 3D tours, get AI valuations, and connect with verified sellers.\n\nBrowse now: ${siteUrl}\n\nSign up free: ${siteUrl}/register?role=buyer`,
    agent: `Join Shopperden as a verified agent. List properties with exclusive rights, get a free trial deal, and manage your pipeline.\n\nRegister here: ${siteUrl}/register?role=agent\n\nFirst deal is free — subscribe to continue.`,
    professional: `Join Shopperden as a verified property professional. Solicitors, surveyors, and mortgage advisers can receive qualified leads.\n\nRegister: ${siteUrl}/register?role=professional`
  };
  const copyMessage = (type) => {
    navigator.clipboard.writeText(messages[type]).then(() => {
      setCopiedType(type);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} invitation copied!`);
      setTimeout(() => setCopiedType(null), 3000);
    });
  };
  return (
    <div className="luxury-card p-6 mb-8" data-testid="invitation-messages">
      <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 font-semibold">Invite People</h3>
      <p className="text-sm text-slate-500 mb-4">Copy invitation messages to share via WhatsApp, Email, or SMS</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(messages).map(([type]) => (
          <button key={type} onClick={() => copyMessage(type)}
            className={`p-3 border text-left transition-all ${copiedType === type ? 'border-green-400 bg-green-50' : 'border-slate-200 hover:border-[#E07A3A] hover:bg-[#FEF3EC]'}`}
            data-testid={`invite-${type}-btn`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold tracking-wider uppercase text-[#1A1A1A]">{type}</span>
              <Share2 className={`h-3.5 w-3.5 ${copiedType === type ? 'text-green-500' : 'text-slate-400'}`} />
            </div>
            <p className="text-[10px] text-slate-400">{copiedType === type ? 'Copied!' : 'Click to copy invite'}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

export const AdminStats = ({ stats }) => (
  <div className="space-y-8" data-testid="admin-overview">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[
        { label: 'Total Users', value: stats.total_users, icon: <Users className="h-5 w-5" /> },
        { label: 'Properties', value: stats.total_properties, icon: <Home className="h-5 w-5" /> },
        { label: 'Transactions', value: stats.total_transactions, icon: <CreditCard className="h-5 w-5" /> },
        { label: 'Pending Verifications', value: stats.pending_verifications, icon: <FileCheck className="h-5 w-5" />, alert: stats.pending_verifications > 0 },
      ].map((stat) => (
        <div key={stat.label} className={`luxury-card p-6 ${stat.alert ? 'border-red-300' : ''}`} data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
          <div className="text-[#E07A3A] mb-3">{stat.icon}</div>
          <p className="text-3xl font-['Playfair_Display'] text-[#1A1A1A]">{stat.value}</p>
          <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
        </div>
      ))}
    </div>
  </div>
);

export const UsersTab = ({ users, onDelete, onRoleChange }) => (
  <div className="space-y-4" data-testid="users-tab">
    {users.map((user) => (
      <div key={user.id} className="luxury-card p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid={`user-row-${user.id}`}>
        <div>
          <p className="text-[#1A1A1A] font-semibold">{user.name}</p>
          <p className="text-sm text-slate-500">{user.email}</p>
          <div className="flex items-center gap-2 mt-1">
            <span
              className="text-[0.7rem] px-2.5 py-0.5 font-bold rounded uppercase tracking-wider"
              style={{ background: '#0F172A', color: '#FACC15', border: '1px solid #FACC15' }}
              data-testid={`user-role-chip-${user.id}`}
            >{user.role}</span>
            {user.id_verified && <span className="text-[10px] tracking-wider uppercase font-bold px-2 py-0.5 rounded border bg-emerald-950/95 text-emerald-300 border-emerald-500/50">ID Verified</span>}
            {user.verification_status && user.verification_status !== 'not_submitted' && (
              <StatusBadge status={user.verification_status} variants={{
                pending_review: { bg: 'bg-amber-100', text: 'text-amber-700' },
                verified: { bg: 'bg-green-100', text: 'text-green-700' },
                rejected: { bg: 'bg-red-100', text: 'text-red-700' },
              }} />
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {user.role !== 'admin' && (
            <select
              value={user.role}
              onChange={(e) => onRoleChange(user.id, user.name, e.target.value)}
              className="border border-slate-200 px-2 py-1 text-xs text-[#0F172A] focus:border-[#E07A3A] focus:outline-none"
              data-testid={`role-select-${user.id}`}
            >
              <option value="buyer">Buyer</option>
              <option value="seller">Seller</option>
              <option value="tenant">Tenant</option>
              <option value="professional">Professional</option>
            </select>
          )}
          {user.role !== 'admin' && (
            <Button onClick={() => onDelete(user.id, user.name)} variant="outline" className="text-red-500 border-red-200 hover:bg-red-50" data-testid={`delete-user-${user.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    ))}
    {users.length === 0 && <p className="text-center text-slate-400 py-8">No users found</p>}
  </div>
);

export const PropertiesTab = ({ properties, onDelete }) => (
  <div className="space-y-4" data-testid="properties-tab">
    {properties.map((property) => (
      <div key={property.id} className="luxury-card p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid={`property-row-${property.id}`}>
        <div className="flex items-center gap-3">
          {property.images?.[0] ? (
            <img src={property.images[0]} alt="" className="w-16 h-12 object-cover flex-shrink-0" />
          ) : (
            <div className="w-16 h-12 bg-slate-100 flex items-center justify-center flex-shrink-0"><Home className="h-5 w-5 text-slate-300" /></div>
          )}
          <div>
            <p className="text-[#1A1A1A] font-semibold">{property.title}</p>
            <p className="text-sm text-slate-500">{property.city || 'N/A'}</p>
          </div>
        </div>
        <Button onClick={() => onDelete(property.id, property.title)} variant="outline" className="text-red-500 border-red-200 hover:bg-red-50" data-testid={`delete-property-${property.id}`}>
          <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
        </Button>
      </div>
    ))}
    {properties.length === 0 && <p className="text-center text-slate-400 py-8">No properties found</p>}
  </div>
);

export const TransactionsTab = ({ transactions }) => (
  <div className="space-y-4" data-testid="transactions-tab">
    {transactions.map((txn, i) => (
      <div key={txn.transaction_id || i} className="luxury-card p-4" data-testid={`txn-row-${i}`}>
        <div className="flex justify-between items-center">
          <div>
            <p className="text-[#1A1A1A] font-semibold">{(txn.fee_type || txn.payment_type || 'N/A').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
            <p className="text-sm text-slate-500">{txn.payer_email || txn.user_email || 'N/A'}</p>
            {txn.created_at && <p className="text-xs text-slate-400 mt-1">{new Date(txn.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>}
          </div>
          <div className="text-right">
            <p className="text-[#E07A3A] font-semibold">{txn.currency?.toUpperCase() === 'GBP' ? '£' : txn.currency?.toUpperCase() === 'INR' ? '₹' : txn.currency?.toUpperCase() === 'CAD' ? 'C$' : '£'}{Number(txn.amount || 0).toLocaleString('en-GB')}</p>
            <StatusBadge status={txn.status} variants={{
              completed: { bg: 'bg-green-100', text: 'text-green-700' },
              pending: { bg: 'bg-amber-100', text: 'text-amber-700' },
              failed: { bg: 'bg-red-100', text: 'text-red-700' },
            }} />
          </div>
        </div>
      </div>
    ))}
    {transactions.length === 0 && <p className="text-center text-slate-400 py-8">No transactions found</p>}
  </div>
);

export const VerificationsTab = ({ verifications, onAction }) => (
  <div className="space-y-4" data-testid="verifications-tab">
    {verifications.map((v) => (
      <div key={v.id} className={`luxury-card p-4 ${v.status === 'pending_review' ? 'border-l-4 border-l-amber-400' : ''}`} data-testid={`verification-${v.id}`}>
        <div className="flex flex-col sm:flex-row justify-between gap-3">
          <div>
            <p className="text-[#1A1A1A] font-semibold">{v.full_name}</p>
            <p className="text-sm text-slate-500">User ID: {v.user_id}</p>
            {v.document_type && <p className="text-xs text-slate-400 mt-1">Document: {v.document_type}</p>}
            <StatusBadge status={v.status} variants={{
              pending_review: { bg: 'bg-amber-100', text: 'text-amber-700' },
              verified: { bg: 'bg-green-100', text: 'text-green-700' },
              rejected: { bg: 'bg-red-100', text: 'text-red-700' },
            }} />
          </div>
          {v.status === 'pending_review' && (
            <div className="flex gap-2">
              <Button onClick={() => onAction(v.user_id, 'verified')} className="bg-green-600 text-white hover:bg-green-700 text-xs" data-testid={`approve-${v.id}`}>
                <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve
              </Button>
              <Button onClick={() => onAction(v.user_id, 'rejected')} variant="outline" className="text-red-500 border-red-200 hover:bg-red-50 text-xs" data-testid={`reject-${v.id}`}>
                <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
              </Button>
            </div>
          )}
        </div>
      </div>
    ))}
    {verifications.length === 0 && <p className="text-center text-slate-400 py-8">No verifications found</p>}
  </div>
);

export const GDPRTab = ({ contactRequests, onAction }) => (
  <div className="space-y-4" data-testid="gdpr-tab">
    {contactRequests.map((req) => (
      <div key={req.id} className={`luxury-card p-4 ${req.admin_status === 'pending' ? 'border-l-4 border-l-amber-400' : ''}`} data-testid={`gdpr-${req.id}`}>
        <div className="flex flex-col sm:flex-row justify-between gap-3">
          <div>
            <p className="text-[#1A1A1A] font-semibold">{req.user_name}</p>
            <p className="text-sm text-slate-500">{req.user_email} → Property: {req.property_id}</p>
            <StatusBadge status={req.admin_status} variants={{
              pending: { bg: 'bg-amber-100', text: 'text-amber-700' },
              approved: { bg: 'bg-green-100', text: 'text-green-700' },
              rejected: { bg: 'bg-red-100', text: 'text-red-700' },
            }} />
          </div>
          {req.admin_status === 'pending' && (
            <div className="flex gap-2">
              <Button onClick={() => onAction(req.id, 'approved')} className="bg-green-600 text-white hover:bg-green-700 text-xs" data-testid={`approve-gdpr-${req.id}`}>
                <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve
              </Button>
              <Button onClick={() => onAction(req.id, 'rejected')} variant="outline" className="text-red-500 border-red-200 hover:bg-red-50 text-xs" data-testid={`reject-gdpr-${req.id}`}>
                <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
              </Button>
            </div>
          )}
        </div>
      </div>
    ))}
    {contactRequests.length === 0 && <p className="text-center text-slate-400 py-8">No GDPR contact requests found</p>}
  </div>
);
